import Cookies from 'universal-cookie';
import { getProfile } from 'Api/AccountApi';
import { logout } from 'Api/ProfileApi';
import { createEntitlementCookie, getSubscriptionWithDelay, removeEntitlementCookie } from 'Utils/Entitlements';
import { identify } from 'Utils/Analytics';
import { clearAll } from 'State/GlobalActions';
import { trackPurchaseSource } from 'State/Tracking/TrackingActions';
import { setSubscriptionPlanProperty, setSubscriptionStateProperty } from 'Utils/Analytics/Amplitude';
import { logger } from 'Utils/Logger';
import { removeUser } from 'Utils/UserCookie';
import { getSheerVerificationId } from 'Utils/SheerIdHelpers';
import { createProfile } from 'Utils/ProfileSDK';
import { createAuthInstance } from 'Utils/AuthSDK';
import {
  SIGN_IN,
  SIGN_IN_MVPD,
  SIGN_OUT,
  PENDING_SIGN_OUT,
  UPDATE_USER_PROFILE,
  FETCHING_USER,
  CLEAR_ENTITLEMENTS,
  UPDATE_ENTITLEMENTS,
} from '../Types';
import { getAnonymousId } from 'Utils/Analytics/SegmentShared';

const MAX_RETRIES = 5;
const cookies = new Cookies();

export const updateUserProfile = (payload) => ({ type: UPDATE_USER_PROFILE, payload });
const userSignIn = (payload) => ({ type: SIGN_IN, payload });
const userSignInMvpd = (payload) => ({ type: SIGN_IN_MVPD, payload });
const userPendingSignOut = () => ({ type: PENDING_SIGN_OUT });
const userSignOut = () => ({ type: SIGN_OUT });
const fetchingUser = () => ({ type: FETCHING_USER });
const updateEntitlements = (payload) => ({ type: UPDATE_ENTITLEMENTS, payload });
export const clearEntitlements = () => ({ type: CLEAR_ENTITLEMENTS });

export const signIn = (user) => (dispatch, getState) => {
  const { lastKnownProfileIdCookieName, cookieDomain } = getState().appConfig.defaultConfig;
  const { profileId } = user;
  logger.started('sign in');
  dispatch(userSignIn(user));
  cookies.set(lastKnownProfileIdCookieName, profileId, { path: '/', domain: cookieDomain });
  /* Passing the anonymousId makes the anonymousId stay consistent before and after sign up.
  https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity */
  identify(profileId, getAnonymousId());
  createProfile();
  logger.setAttr('profile_id', profileId);
  logger.completed('sign in');
};

export const signInMvpd = (user) => (dispatch) => {
  const { mvpd } = user;
  logger.started('sign in mvpd');
  dispatch(userSignInMvpd(user));
  logger.setAttr('mvpd', mvpd);
  logger.completed('sign in mvpd');
};

export const signOut = () => (dispatch, getState) => {
  logger.started('sign out');
  return logout().then(async () => {
    dispatch(userSignOut());
    await removeUser();
    const {
      subscription: { activeSubscriptionStatus },
    } = getState();
    if (activeSubscriptionStatus) {
      setSubscriptionStateProperty(activeSubscriptionStatus, true);
    }
    dispatch(clearAll());
    trackPurchaseSource();
    removeEntitlementCookie();
    identify();
    createProfile();
    createAuthInstance();
    logger.completed('sign out', { activeSubscriptionStatus });
  });
};

export const pendingSignOut = () => (dispatch) => {
  dispatch(userPendingSignOut());
};

export const fetchProfileInfo = () => {
  return async (dispatch, getState) => {
    try {
      const {
        user: { profileId },
      } = getState();
      dispatch(fetchingUser());
      const profileInfo = await getProfile(profileId);
      if (profileInfo && profileInfo.profileId) {
        dispatch(updateUserProfile(profileInfo));
      } else {
        throw new Error('Error: cannot find profileId of ProfileInfo');
      }
    } catch (error) {
      //Necessary to set isFetching to false
      dispatch(updateUserProfile({}));
    }
  };
};

export const getEntitlements =
  (maxRetries = MAX_RETRIES) =>
  async (dispatch, getState) => {
    const {
      appConfig: {
        defaultConfig: { productSkus },
      },
    } = getState();

    const now = Date.now();
    logger.started('subscription poll', { productSkus });
    const { hasSubscription, subscriptions } = await getSubscriptionWithDelay(productSkus, maxRetries);
    const diff = Date.now() - now; // in ms
    logger.completed('subscription poll', { pollTime: diff, hasSubscription, subscriptions });
    if (hasSubscription) {
      const [packageDetails] = subscriptions;
      setSubscriptionPlanProperty(packageDetails);
      trackPurchaseSource('general');
      createEntitlementCookie();
      dispatch(updateEntitlements({ hasEntitlements: hasSubscription, entitlements: subscriptions }));
      return { hasEntitlements: true };
    }
    // no entitlements, remove entitlement cookies
    trackPurchaseSource();
    removeEntitlementCookie();
    return { hasEntitlement: false };
  };
